import React from "react";
import {
  Sun,
  Moon,
  Bed,
  Sunrise,
  Compass,
  Star,
  Sparkles,
} from "lucide-react";

/**
 * Fantasy Clock component with 3 selectable designs.
 * All designs display: hour:minute, day, month name, year, sun/moon icon, night state.
 * Quick action buttons: Short Rest (+1h), Long Rest (+8h).
 *
 * Uses the app color palette only (gold #d4af37, arcane purple, ruby, parchment).
 */
export default function FantasyClock({
  style = "astrolabio",
  scale = 1.0,
  map,
  onShortRest,
  onLongRest,
  onAdvance,
  onEdit,
}) {
  if (!map) return null;

  const hour = Math.floor(map.ig_hour || 0);
  const minute = Math.round(((map.ig_hour || 0) - hour) * 60);
  const day = map.ig_day || 1;
  const monthName = (map.months || [])[(map.ig_month || 1) - 1] || `Mese ${map.ig_month}`;
  const year = map.ig_year || 1;
  const isNight = map.is_night;

  const commonProps = {
    hour,
    minute,
    day,
    monthName,
    year,
    isNight,
    onShortRest,
    onLongRest,
    onAdvance,
  };

  return (
    <div className="w-full" data-testid="fantasy-clock" data-clock-style={style}>
      <button
        type="button"
        onClick={onEdit}
        title="Modifica data e ora manualmente"
        className="w-full block hover:opacity-90 focus:outline-none"
        data-testid="clock-edit-trigger"
      >
        <div style={{ transform: `scale(${scale})`, transformOrigin: "top center" }}>
          {style === "meridiana" && <MeridianaClock {...commonProps} />}
          {style === "cronografo" && <CronografoClock {...commonProps} />}
          {(style !== "meridiana" && style !== "cronografo") && (
            <AstrolabioClock {...commonProps} />
          )}
        </div>
      </button>
      <ClockActions
        onShortRest={onShortRest}
        onLongRest={onLongRest}
        isNight={isNight}
      />
    </div>
  );
}

/* --------------------- DESIGN 1: ASTROLABIO --------------------- */

function AstrolabioClock({ hour, minute, day, monthName, year, isNight }) {
  const totalMin = hour * 60 + minute;
  const hourAngle = (totalMin / (24 * 60)) * 360; // full rotation per day
  const minAngle = (minute / 60) * 360;

  return (
    <div
      className="relative rune-border bg-card/70 rounded-lg p-4 backdrop-blur"
      data-testid="clock-astrolabio"
    >
      <p className="font-heading text-[9px] tracking-widest text-gold uppercase mb-3 text-center">
        ◈ Astrolabio Arcano ◈
      </p>

      <div className="relative w-full aspect-square max-w-[220px] mx-auto">
        {/* Outer ornate ring */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            background:
              "radial-gradient(circle at 30% 30%, rgba(212,175,55,0.18), rgba(0,0,0,0) 60%), radial-gradient(circle at 70% 80%, rgba(167,139,250,0.14), rgba(0,0,0,0) 60%)",
            boxShadow:
              "inset 0 0 20px rgba(212,175,55,0.3), 0 0 20px rgba(212,175,55,0.15)",
            border: "2px solid rgba(212,175,55,0.55)",
          }}
        />
        {/* Inner tick ring with hour marks (Roman numerals for 12 divisions) */}
        <svg
          viewBox="0 0 200 200"
          className="absolute inset-0 w-full h-full"
          style={{ transform: "rotate(-90deg)" }}
        >
          {/* Tick marks (24) */}
          {Array.from({ length: 24 }).map((_, i) => {
            const a = (i / 24) * 2 * Math.PI;
            const inner = i % 6 === 0 ? 78 : i % 2 === 0 ? 82 : 84;
            const outer = 90;
            const x1 = 100 + inner * Math.cos(a);
            const y1 = 100 + inner * Math.sin(a);
            const x2 = 100 + outer * Math.cos(a);
            const y2 = 100 + outer * Math.sin(a);
            return (
              <line
                key={i}
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke={i % 6 === 0 ? "#d4af37" : "rgba(212,175,55,0.5)"}
                strokeWidth={i % 6 === 0 ? 2 : 1}
              />
            );
          })}
          {/* Zodiac-ish etched arc */}
          <circle
            cx="100"
            cy="100"
            r="70"
            fill="none"
            stroke="rgba(212,175,55,0.35)"
            strokeWidth="1"
            strokeDasharray="2 6"
          />
        </svg>

        {/* Roman numerals VI/XII/XVIII/XXIV (cardinal points) */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="relative w-full h-full font-serif-fantasy text-gold text-[10px]">
            <span className="absolute left-1/2 top-3 -translate-x-1/2">XII</span>
            <span className="absolute right-3 top-1/2 -translate-y-1/2">VI</span>
            <span className="absolute left-1/2 bottom-3 -translate-x-1/2">XVIII</span>
            <span className="absolute left-3 top-1/2 -translate-y-1/2">XXIV</span>
          </div>
        </div>

        {/* Center medallion with sun/moon */}
        <div className="absolute inset-[22%] rounded-full flex items-center justify-center overflow-hidden"
          style={{
            background: isNight
              ? "radial-gradient(circle, #1e1b4b 0%, #0f0a1e 100%)"
              : "radial-gradient(circle, #78350f 0%, #451a03 100%)",
            border: "2px solid rgba(212,175,55,0.6)",
            boxShadow: "inset 0 0 20px rgba(0,0,0,0.6)",
          }}
        >
          {isNight ? (
            <Moon className="w-9 h-9 text-arcane" style={{ filter: "drop-shadow(0 0 8px rgba(167,139,250,0.7))" }} />
          ) : (
            <Sun className="w-9 h-9 text-gold" style={{ filter: "drop-shadow(0 0 10px rgba(212,175,55,0.8))" }} />
          )}
        </div>

        {/* Hour hand (long, ornate) */}
        <div
          className="absolute left-1/2 top-1/2 origin-bottom"
          style={{
            width: "3px",
            height: "38%",
            transform: `translate(-50%, -100%) rotate(${hourAngle}deg)`,
            transformOrigin: "50% 100%",
            background:
              "linear-gradient(to top, #d4af37, #fef3c7)",
            borderRadius: "2px",
            boxShadow: "0 0 6px rgba(212,175,55,0.7)",
          }}
        />
        {/* Minute hand */}
        <div
          className="absolute left-1/2 top-1/2 origin-bottom"
          style={{
            width: "2px",
            height: "44%",
            transform: `translate(-50%, -100%) rotate(${minAngle}deg)`,
            transformOrigin: "50% 100%",
            background: "linear-gradient(to top, rgba(167,139,250,0.9), #f5deb3)",
            borderRadius: "2px",
          }}
        />
        {/* Center pin */}
        <div
          className="absolute left-1/2 top-1/2 w-3 h-3 rounded-full -translate-x-1/2 -translate-y-1/2"
          style={{
            background: "#d4af37",
            boxShadow: "0 0 6px rgba(212,175,55,0.9)",
            border: "1px solid #000",
          }}
        />
      </div>

      <ClockReadout hour={hour} minute={minute} day={day} monthName={monthName} year={year} />
    </div>
  );
}

/* --------------------- DESIGN 2: MERIDIANA --------------------- */

function MeridianaClock({ hour, minute, day, monthName, year, isNight }) {
  // Sundial: gnomon casts shadow based on time of day (6 = full left, 18 = full right)
  // Map hour 0..24 to angle -90..90 for the gnomon shadow
  const dayAngle = ((hour + minute / 60) / 24) * 180 - 90;

  return (
    <div
      className="relative rune-border bg-card/70 rounded-lg p-4 backdrop-blur"
      data-testid="clock-meridiana"
    >
      <p className="font-heading text-[9px] tracking-widest text-gold uppercase mb-3 text-center">
        ◈ Meridiana del Viandante ◈
      </p>

      {/* Semi-circular sundial */}
      <div className="relative w-full max-w-[240px] mx-auto aspect-[2/1] overflow-hidden">
        <svg viewBox="0 0 200 100" className="w-full h-full">
          <defs>
            <radialGradient id="sunset" cx="50%" cy="100%" r="80%">
              <stop offset="0%" stopColor={isNight ? "#1e1b4b" : "#f59e0b"} stopOpacity="0.9" />
              <stop offset="70%" stopColor={isNight ? "#0f0a1e" : "#451a03"} stopOpacity="1" />
              <stop offset="100%" stopColor="#000" stopOpacity="1" />
            </radialGradient>
            <linearGradient id="goldFill" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#fef3c7" />
              <stop offset="100%" stopColor="#d4af37" />
            </linearGradient>
          </defs>

          {/* Sundial dome */}
          <path d="M 10 100 A 90 90 0 0 1 190 100 Z" fill="url(#sunset)" stroke="#d4af37" strokeWidth="2" />

          {/* Hour lines */}
          {[0, 3, 6, 9, 12, 15, 18, 21, 24].map((h, i) => {
            const angle = (h / 24) * 180 - 90;
            const rad = (angle * Math.PI) / 180;
            const inner = 60;
            const outer = 88;
            const x1 = 100 + inner * Math.sin(rad);
            const y1 = 100 - inner * Math.cos(rad);
            const x2 = 100 + outer * Math.sin(rad);
            const y2 = 100 - outer * Math.cos(rad);
            const isMajor = h % 6 === 0;
            return (
              <g key={i}>
                <line
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={isMajor ? "#d4af37" : "rgba(212,175,55,0.5)"}
                  strokeWidth={isMajor ? 1.6 : 0.8}
                />
                {isMajor && (
                  <text
                    x={100 + 52 * Math.sin(rad)}
                    y={100 - 52 * Math.cos(rad) + 3}
                    fill="#d4af37"
                    fontSize="7"
                    fontFamily="Cinzel"
                    textAnchor="middle"
                  >
                    {h}
                  </text>
                )}
              </g>
            );
          })}

          {/* Constellations at night, sun rays at day */}
          {isNight
            ? [[30, 40], [60, 30], [140, 35], [170, 42], [90, 55]].map(([x, y], i) => (
                <g key={i}>
                  <circle cx={x} cy={y} r="1.3" fill="#e0e7ff" />
                  <circle cx={x} cy={y} r="2.8" fill="#a78bfa" opacity="0.25" />
                </g>
              ))
            : [[30, 45], [55, 30], [100, 22], [145, 30], [170, 45]].map(([x, y], i) => (
                <circle key={i} cx={x} cy={y} r="1.4" fill="#fef3c7" opacity="0.8" />
              ))}

          {/* Sun/Moon disc positioned along the arc */}
          {(() => {
            const rad = (dayAngle * Math.PI) / 180;
            const cx = 100 + 70 * Math.sin(rad);
            const cy = 100 - 70 * Math.cos(rad);
            return (
              <g>
                <circle cx={cx} cy={cy} r="9" fill={isNight ? "#e0e7ff" : "#fbbf24"} opacity="0.35" />
                <circle
                  cx={cx}
                  cy={cy}
                  r="6"
                  fill={isNight ? "#c7d2fe" : "#fef3c7"}
                  stroke={isNight ? "#a78bfa" : "#d4af37"}
                  strokeWidth="1.2"
                />
              </g>
            );
          })()}

          {/* Gnomon (central triangle casting shadow) */}
          <polygon points="98,100 102,100 100,45" fill="url(#goldFill)" stroke="#000" strokeWidth="0.5" />
          {/* Shadow */}
          <line
            x1="100"
            y1="100"
            x2={100 + 55 * Math.sin((dayAngle * Math.PI) / 180)}
            y2={100 - 55 * Math.cos((dayAngle * Math.PI) / 180)}
            stroke={isNight ? "rgba(167,139,250,0.7)" : "rgba(0,0,0,0.75)"}
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>
      </div>

      <ClockReadout hour={hour} minute={minute} day={day} monthName={monthName} year={year} />
    </div>
  );
}

/* --------------------- DESIGN 3: CRONOGRAFO --------------------- */

function CronografoClock({ hour, minute, day, monthName, year, isNight }) {
  // Hourglass style with parchment date panel
  const dayProgress = (hour + minute / 60) / 24;
  return (
    <div
      className="relative rune-border bg-card/70 rounded-lg p-4 backdrop-blur"
      data-testid="clock-cronografo"
    >
      <p className="font-heading text-[9px] tracking-widest text-gold uppercase mb-3 text-center">
        ◈ Cronografo del Viandante ◈
      </p>

      <div className="flex items-center gap-3 justify-center mb-3">
        {/* Hourglass */}
        <div className="relative w-24 h-32 shrink-0">
          <svg viewBox="0 0 80 110" className="w-full h-full">
            <defs>
              <linearGradient id="sand" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#fef3c7" />
                <stop offset="100%" stopColor="#d4af37" />
              </linearGradient>
            </defs>
            {/* Frame */}
            <rect x="6" y="4" width="68" height="6" rx="2" fill="#78350f" stroke="#d4af37" strokeWidth="1.5" />
            <rect x="6" y="100" width="68" height="6" rx="2" fill="#78350f" stroke="#d4af37" strokeWidth="1.5" />
            {/* Glass silhouette */}
            <path
              d="M 14 12 L 66 12 L 66 20 L 44 52 L 44 58 L 66 90 L 66 98 L 14 98 L 14 90 L 36 58 L 36 52 L 14 20 Z"
              fill="rgba(212,175,55,0.06)"
              stroke="#d4af37"
              strokeWidth="1.5"
            />
            {/* Sand top (empties as day passes) */}
            <path
              d={`M 15 ${12 + dayProgress * 30} L 65 ${12 + dayProgress * 30} L 44 52 L 36 52 Z`}
              fill="url(#sand)"
              opacity={1 - dayProgress * 0.4}
            />
            {/* Sand bottom (fills as day passes) */}
            <path
              d={`M 15 98 L 65 98 L 65 ${98 - dayProgress * 30} L 36 58 L 44 58 Z`}
              fill="url(#sand)"
            />
            {/* Stream */}
            {dayProgress > 0 && dayProgress < 1 && (
              <line
                x1="40"
                y1="54"
                x2="40"
                y2="60"
                stroke="#fef3c7"
                strokeWidth="1"
              />
            )}
          </svg>
        </div>

        {/* Sun/Moon medallion */}
        <div
          className="w-20 h-20 rounded-full flex items-center justify-center relative"
          style={{
            background: isNight
              ? "radial-gradient(circle, #1e1b4b 0%, #0f0a1e 100%)"
              : "radial-gradient(circle, #78350f 0%, #451a03 100%)",
            border: "2px solid rgba(212,175,55,0.6)",
            boxShadow: "0 0 12px rgba(212,175,55,0.3), inset 0 0 15px rgba(0,0,0,0.5)",
          }}
        >
          {isNight ? (
            <Moon
              className="w-10 h-10 text-arcane"
              style={{ filter: "drop-shadow(0 0 8px rgba(167,139,250,0.8))" }}
            />
          ) : (
            <Sun
              className="w-10 h-10 text-gold"
              style={{ filter: "drop-shadow(0 0 10px rgba(212,175,55,0.9))" }}
            />
          )}
          {/* Rune inscriptions around */}
          {["✦", "☾", "✧", "☼"].map((r, i) => {
            const angles = [0, 90, 180, 270];
            const rad = (angles[i] * Math.PI) / 180;
            return (
              <span
                key={i}
                className="absolute text-gold text-xs"
                style={{
                  left: `calc(50% + ${Math.cos(rad) * 38}px - 6px)`,
                  top: `calc(50% + ${Math.sin(rad) * 38}px - 8px)`,
                  textShadow: "0 0 6px rgba(212,175,55,0.7)",
                }}
              >
                {r}
              </span>
            );
          })}
        </div>
      </div>

      <ClockReadout hour={hour} minute={minute} day={day} monthName={monthName} year={year} />
    </div>
  );
}

/* --------------------- SHARED READOUT & ACTIONS --------------------- */

function ClockReadout({ hour, minute, day, monthName, year }) {
  const hh = String(hour).padStart(2, "0");
  const mm = String(minute).padStart(2, "0");
  return (
    <div
      className="mt-3 pt-3 border-t border-gold/25 text-center"
      data-testid="clock-readout"
    >
      <p
        className="font-display text-3xl text-gold leading-none"
        style={{
          textShadow: "0 0 12px rgba(212,175,55,0.35)",
          fontFeatureSettings: '"lnum" on, "tnum" on',
        }}
      >
        {hh}:{mm}
      </p>
      <p className="font-serif-fantasy italic text-parchment text-sm mt-1">
        {day}° di <span className="text-gold">{monthName}</span>
      </p>
      <p className="font-heading text-[10px] tracking-widest text-muted-foreground uppercase mt-0.5">
        Anno {year} · Era Fantasy
      </p>
    </div>
  );
}

function ClockActions({ onShortRest, onLongRest, isNight }) {
  return (
    <div className="mt-3 grid grid-cols-2 gap-2" data-testid="clock-actions">
      <button
        onClick={onShortRest}
        className="group flex items-center justify-center gap-1.5 py-2 px-2 rounded border border-gold/40 hover:border-gold hover:bg-gold/10 transition-all text-gold font-heading text-[10px] tracking-widest uppercase"
        title="Riposo Breve (+1 ora)"
        data-testid="short-rest-btn"
      >
        <Sunrise className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
        Riposo Breve
      </button>
      <button
        onClick={onLongRest}
        className="group flex items-center justify-center gap-1.5 py-2 px-2 rounded border border-arcane/40 hover:border-arcane hover:bg-arcane/10 transition-all text-arcane font-heading text-[10px] tracking-widest uppercase"
        title="Riposo Lungo (+8 ore)"
        data-testid="long-rest-btn"
      >
        <Bed className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
        Riposo Lungo
      </button>
    </div>
  );
}
